import React, { useContext, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { PlusIcon } from '@heroicons/react/24/solid';
import { UserContext } from "../App";
import { db } from '../firebase';
import { collection, getDocs, doc, setDoc, addDoc, updateDoc } from 'firebase/firestore';
import { ArchiveBoxIcon, ChevronDownIcon, PencilIcon } from '@heroicons/react/24/outline';
import AddAccountModal from './AddAccountModal';
import RenameAccountModal from './RenameAccountModal';
import AnnouncementCenter from './AnnouncementCenter';
import { getNavGroups, isAdminEmail, adminNavItem } from './navConfig';
import { buildAccountRecord, getAccountPhaseLabel } from '../utils/accountLifecycle';

// Sidebar width constant for Layout.js to import
export const SIDEBAR_WIDTH = 160;

const NavBar = () => {
  const { accounts, selectedAccount, setSelectedAccount, setAccounts, refreshAccounts, currentUser, setShowDeleteConfirm, setAccountToDelete, triggerDataRefresh, accountsLoaded = true } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = isAdminEmail(currentUser?.email);
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const accountMenuRef = useRef(null);

  const handleRenameAccount = async (newName) => {
    if (!currentUser || !selectedAccount) return;
    const accountRef = doc(db, 'users', currentUser.uid, 'accounts', selectedAccount.id);
    await updateDoc(accountRef, { name: newName });
    await refreshAccounts();
    setSelectedAccount({ ...selectedAccount, name: newName });
  };

  // Close account menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setShowAccountMenu(false);
      }
    };

    if (showAccountMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showAccountMenu]);

  // Get current month path for Calendar navigation
  const now = new Date();
  const currentMonthPath = `/month/${now.getFullYear()}/${now.getMonth() + 1}`;

  const navGroups = getNavGroups(currentMonthPath);

  // Quick Add handler
  const handleQuickAdd = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const day = now.getDate();
    const returnPath = location.pathname;
    navigate(`/day/${year}/${month}/${day}`, {
      state: {
        date: now.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }),
        directAdd: true,
        returnPath,
      },
    });
  };

  // Helper function to determine if a nav item should be active
  const isNavItemActive = (item) => {
    if (!item.path) return false;
    
    // Check for special matchPaths (like Calendar which matches /, /month, /day)
    if (item.matchPaths) {
      return item.matchPaths.some(matchPath => {
        if (matchPath === "/") {
          return location.pathname === "/";
        }
        return location.pathname.startsWith(matchPath);
      });
    }
    
    // Exact match
    if (location.pathname === item.path) return true;
    
    // Prefix match for nested routes
    if (location.pathname.startsWith(item.path + "/")) return true;
    
    return false;
  };

  const navButtonBaseClass = "sidebar-nav-button w-full flex items-center gap-2 rounded-xl border border-transparent px-2 py-1 text-left text-[12.5px] font-medium transition-all duration-150";
  const getNavButtonClassName = (isActive, extraClassName = "") =>
    `${navButtonBaseClass} ${isActive ? 'theme-nav-item-active' : 'theme-nav-item'} ${extraClassName}`.trim();
  const getNavButtonStyle = (isActive) => (
    isActive
      ? {
          borderColor: 'var(--theme-accent-soft)',
          boxShadow: '0 1px 2px var(--theme-shadow)',
        }
      : undefined
  );

  // Handle account creation with automatic deposit entry
  const handleCreateAccount = async (accountData) => {
    if (!currentUser) throw new Error('User not authenticated');

    const { balance } = accountData;

    // Create account document
    const accountsCol = collection(db, 'users', currentUser.uid, 'accounts');
    const accountRecord = buildAccountRecord(accountData);
    const docRef = await addDoc(accountsCol, accountRecord);

    // Automatically create deposit entry
    const now = new Date();
    const entriesCol = collection(db, 'users', currentUser.uid, 'accounts', docRef.id, 'entries');
    await addDoc(entriesCol, {
      pnl: Number(balance), // Store deposit amount in pnl field for consistency
      notes: 'Initial deposit',
      created: now.toISOString(),
      isDeposit: true,
      accountBalance: Number(balance), // Initial balance equals deposit amount
      year: now.getFullYear().toString(),
      month: (now.getMonth() + 1).toString(),
      day: now.getDate().toString(),
    });

    // Refresh accounts from Firestore
    if (refreshAccounts) {
      await refreshAccounts();
      // Fetch accounts directly to get the latest list
      const accountsCol2 = collection(db, 'users', currentUser.uid, 'accounts');
      const snap = await getDocs(accountsCol2);
      const allAccounts = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const refreshedNewAccount = allAccounts.find(acc => acc.id === docRef.id);
      if (refreshedNewAccount) {
        setSelectedAccount(refreshedNewAccount);
        // Save selected account preference
        const prefsRef = doc(db, 'users', currentUser.uid, 'preferences', 'settings');
        await setDoc(prefsRef, { selectedAccountId: refreshedNewAccount.id }, { merge: true });
      }
    } else {
      // Fallback: add to local state if refreshAccounts not available
      const newAccount = { 
        id: docRef.id, 
        ...accountRecord,
      };
      setAccounts(prev => [...prev, newAccount]);
      setSelectedAccount(newAccount);
      // Save selected account preference
      try {
        const prefsRef = doc(db, 'users', currentUser.uid, 'preferences', 'settings');
        await setDoc(prefsRef, { selectedAccountId: newAccount.id }, { merge: true });
      } catch (error) {
        console.error('Error saving selected account:', error);
      }
    }

    // Trigger data refresh to update Summary and Trades pages
    if (triggerDataRefresh) {
      triggerDataRefresh();
    }

  };

  return (
    <aside
      className="sidebar-balanced fixed top-0 left-0 h-full z-[60] theme-nav-shell border-r theme-border flex flex-col shadow-lg"
      style={{ width: SIDEBAR_WIDTH }}
    >
      {/* Account Section - Top */}
      <div className="sidebar-account px-2.5 pt-2 pb-1.5 border-b theme-border">
        <div className="flex items-center gap-1">
          <div className="min-w-0 flex-1">
            {accounts && accounts.length > 0 ? (
              <div className="relative" ref={accountMenuRef}>
            {/* Clickable Account Row */}
            <button
              onClick={() => setShowAccountMenu(!showAccountMenu)}
              className="w-full flex items-center justify-between gap-1.5 px-2.5 py-1.5 theme-input theme-hover-surface font-medium rounded-lg border transition-colors text-[11px] shadow-sm"
            >
              <span className="truncate flex-1 text-left">
                {selectedAccount ? selectedAccount.name || 'Account' : 'No Account'}
              </span>
              <ChevronDownIcon 
                className={`w-2.5 h-2.5 theme-text-muted transition-transform flex-shrink-0 ${
                  showAccountMenu ? 'rotate-180' : ''
                }`} 
              />
            </button>
            
            {/* Popover Menu */}
            {showAccountMenu && (
              <div className="absolute top-full left-0 right-0 mt-1.5 theme-nav-popover border theme-border rounded-xl shadow-lg z-50 overflow-hidden">
                {/* Account List */}
                <div className="max-h-48 overflow-y-auto themed-scrollbar">
                  {accounts.map((acc) => (
                    <button
                      key={acc.id}
                      onClick={async () => {
                        setSelectedAccount(acc);
                        setShowAccountMenu(false);
                        // Save selected account preference
                        try {
                          const prefsRef = doc(db, 'users', currentUser.uid, 'preferences', 'settings');
                          await setDoc(prefsRef, { selectedAccountId: acc.id }, { merge: true });
                        } catch (error) {
                          console.error('Error saving selected account:', error);
                        }
                      }}
                      className={`w-full flex items-center justify-between gap-1.5 px-2.5 py-1.5 text-[11px] text-left transition-colors ${
                        selectedAccount?.id === acc.id 
                          ? 'theme-nav-item-active'
                          : 'theme-nav-item'
                      }`}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{acc.name || 'Account'}</span>
                        <span className="block truncate text-[9px] opacity-70">{getAccountPhaseLabel(acc)}</span>
                      </span>
                      {selectedAccount?.id === acc.id && (
                        <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 ml-2" style={{ backgroundColor: 'var(--theme-accent)' }} />
                      )}
                    </button>
                  ))}
                </div>
                
                {/* Divider */}
                <div className="border-t theme-border my-1" />
                
                {/* Add Account Option */}
                <button
                  onClick={() => {
                    setShowAddAccountModal(true);
                    setShowAccountMenu(false);
                  }}
                  className="w-full flex items-center gap-1 px-2.5 py-1.5 text-[11px] theme-accent theme-hover-surface transition-colors"
                >
                  <PlusIcon className="w-3 h-3" />
                  Add Account
                </button>

                {/* Rename Account Option */}
                {selectedAccount && (
                  <>
                    <div className="border-t theme-border my-1" />
                    <button
                      onClick={() => {
                        setShowRenameModal(true);
                        setShowAccountMenu(false);
                      }}
                      className="w-full flex items-center gap-1 px-2.5 py-1.5 text-[11px] theme-accent theme-hover-surface transition-colors"
                    >
                      <PencilIcon className="w-3 h-3" />
                      Rename account
                    </button>
                  </>
                )}
                
                {/* Archive Account Option */}
                {selectedAccount && (
                  <>
                    <div className="border-t theme-border my-1" />
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        setAccountToDelete(selectedAccount);
                        setShowDeleteConfirm(true);
                        setShowAccountMenu(false);
                      }}
                      className="w-full flex items-center gap-1 px-2.5 py-1.5 text-[11px] theme-danger-soft hover:opacity-90 transition-colors"
                    >
                      <ArchiveBoxIcon className="w-3 h-3" />
                      Archive Account
                    </button>
                  </>
                )}
              </div>
            )}
              </div>
            ) : !accountsLoaded ? (
              <div className="h-[30px] w-full animate-pulse rounded-lg border theme-border theme-input" aria-hidden="true" />
            ) : (
              <button
                onClick={() => setShowAddAccountModal(true)}
                className="w-full flex items-center justify-center gap-1 px-2.5 py-1.5 text-[11px] font-medium rounded-lg transition-colors shadow-sm theme-btn-primary"
              >
                <PlusIcon className="w-2.5 h-2.5" />
                Add Account
              </button>
            )}
          </div>
          <AnnouncementCenter />
        </div>
      </div>

      {/* Quick Add CTA - Prominent */}
      <div className="sidebar-quick px-2.5 pt-2.5 pb-0">
        <button
          onClick={handleQuickAdd}
          className="w-full flex items-center gap-2 px-2.5 py-1.5 text-[11px] font-semibold rounded-lg transition-colors shadow-sm theme-btn-primary"
        >
          <span className="flex h-4 w-4 items-center justify-center flex-shrink-0">
            <PlusIcon className="w-3 h-3" />
          </span>
          <span className="truncate">Quick Add</span>
        </button>
      </div>

      {/* Navigation Items */}
      <nav className="sidebar-navigation flex-1 px-2.5 pt-2 pb-2 overflow-y-auto themed-scrollbar">
        <div className="sidebar-navigation-inner">
          {navGroups.map((group, groupIndex) => (
            <div
              key={group.label || groupIndex}
              className={`sidebar-nav-group ${groupIndex === 0 ? '' : 'mt-2.5 pt-0.5'}`}
            >
              {groupIndex > 0 && (
                <div className="sidebar-group-divider mx-2 mb-1.5 border-t theme-border" />
              )}
              {group.items.map((item) => {
                const isActive = isNavItemActive(item);
                return (
                  <React.Fragment key={item.path}>
                    <button
                      onClick={() => navigate(item.path)}
                      className={getNavButtonClassName(isActive)}
                      style={getNavButtonStyle(isActive)}
                    >
                      <span
                        className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg ${
                          isActive ? 'theme-accent' : 'theme-text-muted'
                        }`}
                      >
                        {item.icon}
                      </span>
                      <span className="truncate leading-none">{item.label}</span>
                    </button>
                    {item.dividerAfter && (
                      <div className="sidebar-item-divider mx-2 my-1 border-t theme-border" />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          ))}

          {isAdmin && (
            <div className="sidebar-final-group mt-3.5 pt-1">
              <div className="sidebar-group-divider mx-2 mb-1 border-t theme-border" />
              {/* Admin Panel - Only visible to admin, placed at bottom */}
              <button
                onClick={() => navigate(adminNavItem.path)}
                className={getNavButtonClassName(isNavItemActive({ path: adminNavItem.path }))}
                style={getNavButtonStyle(isNavItemActive({ path: adminNavItem.path }))}
              >
                <span className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg ${
                  isNavItemActive({ path: adminNavItem.path }) ? 'theme-accent' : 'theme-text-muted'
                }`}>
                  {adminNavItem.icon}
                </span>
                <span className="truncate leading-none">{adminNavItem.label}</span>
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Bottom section - can add user info or logout later */}
      <div className="sidebar-footer px-2.5 py-1.5 border-t theme-border">
        <div className="text-[11px] leading-none theme-text-muted text-center">
          Bandz Journal
        </div>
      </div>

      {/* Add Account Modal */}
      <AddAccountModal
        isOpen={showAddAccountModal}
        onClose={() => setShowAddAccountModal(false)}
        onSave={handleCreateAccount}
        currentUser={currentUser}
      />

      {/* Rename Account Modal */}
      <RenameAccountModal
        isOpen={showRenameModal}
        onClose={() => setShowRenameModal(false)}
        account={selectedAccount}
        onSave={handleRenameAccount}
      />
    </aside>
  );
};

export default NavBar;
